import React from 'react';
import { initialize } from 'redux-form';
import shortid from 'shortid';
import _ from 'lodash';
import moment from 'moment';

import { loading, success, handleErrorException, error } from 'State/actions';
import { EDIT_MENU_FORM_NAME } from 'Common/FormEnum';
import {
  EditMenuFieldEnum as EditMenuFields,
  CycleMenuDetailsFieldEnum as CycleMenuDetailsFields,
  CycleMenuRecipeFieldEnum as CycleMenuRecipeFields,
  GetAppliedMenuFieldEnum as GetAppliedMenuFields,
  WriteAppliedMenuFieldEnum as WriteAppliedMenuFields,
} from 'Common/FieldEnum';
import { getRecipes } from 'Common/Components/RecipeSearch/state/selectors';
import { setSaveSuccessful } from 'Pages/managemenu/state/listcycles/actions';
import { getSelectedRecipesById } from 'Pages/managemenu/state/common/selectors';
import * as Utilities from 'Pages/managemenu/subpages/editmenu/utilities';
import {
  MESSAGES,
  DIGITAL_MENU_SUITE_OPTIONS,
} from 'Pages/managemenu/helpers/Constants';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import { RecipeCategoryEnum } from 'Common/Constants/Enumeration/RecipeCategoryEnum';
import { isRequestCancelation } from 'Common/Helpers';
import * as ProdRecordHelpers from 'Pages/manageproduction/helpers/productionRecord';

import { VIEW_BY } from '../../subpages/editmenu/constants';
import * as EditCycleMenuSelectors from './selectors';
import * as CommonSelectors from '../common/selectors';
import * as types from './types';

/**
 * Adds the recipe to the cycle menu day.
 *
 * @param {Number} menuId the cycle menu id
 * @param {Number} recipeIndex the recipe's index in the recipe search list
 * @param {Number} servings the total projected servings
 * @param {Object} droppableDestination
 * @param {String} droppableDestination.droppableId - the droppable id for the cycle menu day
 * @param {Number} droppableDestination.index - the index for the recipe
 * @returns {Boolean} true if added, false otherwise
 */
export const addRecipeToMenu =
  ({ menuId, recipeIndex, servings, droppableDestination }) =>
  (dispatch, getState) => {
    const state = getState();
    const recipes = getRecipes(state);
    const viewBy = EditCycleMenuSelectors.getCycleMenuViewBy(state);
    const categories = CommonSelectors.getCategoriesForView(state, viewBy);
    let recipe = recipes[recipeIndex] || null;
    const options = { alacarteCategory: viewBy !== VIEW_BY.DAY };

    if (recipe) {
      if (!Utilities.hasValidServingSize(recipe)) {
        dispatch(
          error(types.SET_ERROR, MESSAGES.ADD_RECIPE_INVALID_SERVING_SIZE)
        );
        return false;
      }

      const dayNumber = droppableDestination.droppableId;
      const menuDaysMap = _.cloneDeep(
        EditCycleMenuSelectors.getMenuDaysMap(state)
      );

      let recipesById = EditCycleMenuSelectors.getRecipesById(state);
      const recipeCountByDayMap = {
        ...EditCycleMenuSelectors.getRecipeCountByDayMap(state),
      };

      const cycleMenuRecipe = Utilities.convertRecipeToMenuItem({
        menuId,
        recipe,
        dayNumber,
        servings,
      });

      let recipeDraggableIds = menuDaysMap[dayNumber];
      const foundRecipe = Utilities.findRecipe(
        recipesById,
        cycleMenuRecipe,
        recipeDraggableIds
      );

      if (foundRecipe.reimbursable && foundRecipe.alacarte) {
        dispatch(error(types.SET_ERROR, MESSAGES.RECIPE_ALREADY_ADDED));
        return false;
      } else if (foundRecipe.reimbursable && !foundRecipe.alacarte) {
        cycleMenuRecipe.DisplayOnCalendar = false;
        cycleMenuRecipe.Alacarte = true;
      } else {
        cycleMenuRecipe.DisplayOnCalendar = Utilities.getRecipeDisplayOnMenu(
          recipesById,
          cycleMenuRecipe[CycleMenuRecipeFields.RecipeId]
        );
      }

      const id = shortid.generate();

      const mappedRecipes = Utilities.mapIdsToRecipe(
        recipeDraggableIds,
        recipesById
      );

      const maxOrderNum = Utilities.maxOrderNumForCategory(
        mappedRecipes,
        recipe.RecipeCategoryId
      );

      const numCategories = Utilities.countCategories(
        mappedRecipes,
        Math.min(droppableDestination.index, maxOrderNum)
      );

      const index = droppableDestination.index - numCategories;

      recipeDraggableIds = Utilities.add(recipeDraggableIds, id, index);
      menuDaysMap[dayNumber] = recipeDraggableIds;

      recipesById[id] = {
        ...cycleMenuRecipe,
      };

      const result = Utilities.updateMapAndId(
        menuDaysMap,
        recipesById,
        categories,
        options
      );
      Utilities.incrementRecipeCount(
        recipeCountByDayMap,
        dayNumber,
        cycleMenuRecipe.RecipeId
      );
      dispatch({
        type: types.ADD_RECIPE_TO_MENU,
        payload: {
          menuDaysMap: result.menuDaysMap,
          recipesById: result.recipesById,
          recipeCountByDayMap,
        },
      });
      dispatch(createAndAddReimbMeal({ recipe: cycleMenuRecipe, dayNumber }));
      return true;
    }
    return false;
  };

/**
 * Moves the recipe from a cycle menu day to the target cycle menu day.
 *
 * @param {Object} droppableSource the droppable source
 * @param {Number} droppableSource.index - the index of the item being moved
 * @param {String} droppableSource.droppableId - from day number
 * @param {Object} droppableDestination the droppable destination
 * @param {Number} droppableDestination.index - the new index for the item
 * @param {Number} droppableDestination.droppableId - to day number
 * @returns {Boolean} true if added, false otherwise
 */
export const moveRecipe =
  ({ droppableSource, droppableDestination, draggableId }) =>
  (dispatch, getState) => {
    const state = getState();
    let menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);
    let recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const recipeCountByDayMap = {
      ...EditCycleMenuSelectors.getRecipeCountByDayMap(state),
    };
    const viewBy = EditCycleMenuSelectors.getCycleMenuViewBy(state);
    const categories = CommonSelectors.getCategoriesForView(state, viewBy);
    const options = { alacarteCategory: viewBy !== VIEW_BY.DAY };
    const fromDay = droppableSource.droppableId;
    const toDay = droppableDestination.droppableId;
    const sourceDraggableIds = menuDaysMap[fromDay];
    const destDraggableIds = menuDaysMap[toDay];

    let recipe = recipesById[draggableId];
    const foundRecipe = Utilities.findRecipe(
      recipesById,
      recipe,
      destDraggableIds
    );

    if (foundRecipe.reimbursable && foundRecipe.alacarte) {
      dispatch(error(types.SET_ERROR, MESSAGES.RECIPE_ALREADY_ADDED));
      return false;
    } else if (foundRecipe.reimbursable && !foundRecipe.alacarte) {
      recipesById = { ...recipesById };
      recipesById[draggableId] = {
        ...recipe,
        DisplayOnCalendar: false,
        Alacarte: true,
      };
    } else if (!foundRecipe.reimbursable && foundRecipe.alacarte) {
      recipesById[draggableId] = {
        ...recipe,
        DisplayOnCalendar: true,
        Alacarte: false,
      };
    }

    const mappedDest = Utilities.mapIdsToRecipe(destDraggableIds, recipesById);

    const maxOrderNumDest = Utilities.maxOrderNumForCategory(
      mappedDest,
      recipe.RecipeCategoryId
    );

    const destCategoryCount = Utilities.countCategories(
      mappedDest,
      Math.min(droppableDestination.index, maxOrderNumDest)
    );

    const destIndex = droppableDestination.index - destCategoryCount;
    const sourceIndex = sourceDraggableIds.indexOf(draggableId);

    const result = Utilities.move(
      sourceDraggableIds,
      destDraggableIds,
      sourceIndex,
      destIndex
    );

    Utilities.decrementRecipeCount(
      recipeCountByDayMap,
      fromDay,
      recipe.RecipeId
    );
    Utilities.incrementRecipeCount(recipeCountByDayMap, toDay, recipe.RecipeId);

    menuDaysMap[fromDay] = result.source;
    menuDaysMap[toDay] = result.dest;

    const results = Utilities.updateMapAndId(
      menuDaysMap,
      recipesById,
      categories,
      options
    );

    dispatch({
      type: types.MOVE_RECIPE,
      payload: {
        menuDaysMap: results.menuDaysMap,
        recipesById: results.recipesById,
        recipeCountByDayMap,
      },
    });
    dispatch(moveReimbMealRecipe(fromDay, toDay, recipe));
    return true;
  };

export const combineRecipe =
  ({ from, fromDay, to }) =>
  (dispatch, getState) => {
    const state = getState();
    const recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);
    const viewBy = EditCycleMenuSelectors.getCycleMenuViewBy(state);
    const categories = CommonSelectors.getCategoriesForView(state, viewBy);
    const servings = EditCycleMenuSelectors.getServings(state);
    const recipeCountByDayMap = {
      ...EditCycleMenuSelectors.getRecipeCountByDayMap(state),
    };

    const parentRecipe = {
      ...recipesById[to],
      IsParent: true,
    };

    const childRecipe = {
      ...recipesById[from],
      IsParent: false,
      ProjectedServings: parentRecipe.ProjectedServings,
      Alacarte: parentRecipe.Alacarte || false,
      ServingsPercentage: String(
        Utilities.calculateServingsPercentage({
          servings,
          projectedServings: parentRecipe.ProjectedServings,
        })
      ),
    };

    const validAttachment = Utilities.isValidAttachment(
      parentRecipe,
      childRecipe,
      recipesById
    );

    if (!validAttachment) {
      return false;
    }

    parentRecipe.Children.push(from);
    Utilities.decrementRecipeCount(
      recipeCountByDayMap,
      fromDay,
      childRecipe.RecipeId
    );

    recipesById[to] = parentRecipe;
    recipesById[from] = childRecipe;

    menuDaysMap[fromDay] = menuDaysMap[fromDay].filter((id) => id !== from);

    const options = { alacarteCategory: viewBy !== VIEW_BY.DAY };

    const resultMapAndId = Utilities.updateMapAndId(
      menuDaysMap,
      recipesById,
      categories,
      options
    );

    dispatch({
      type: types.COMBINE_ITEMS,
      payload: {
        menuDaysMap: resultMapAndId.menuDaysMap,
        recipesById: resultMapAndId.recipesById,
        recipeCountByDayMap,
      },
    });

    dispatch(
      addRecipeToMealCombine({
        isParentAlacarte: parentRecipe.Alacarte,
        mealRecipeId: parentRecipe.RecipeId,
        toDay: parentRecipe.DayNumber,
        fromDay,
        recipe: childRecipe,
      })
    );

    return true;
  };

export const addRecipeToMealCombine =
  ({ isParentAlacarte, mealRecipeId, toDay, fromDay, recipe }) =>
  (dispatch, getState) => {
    const state = getState();
    const recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };
    const recipeMap = {
      ...EditCycleMenuSelectors.getRecipeMapOnDay(state)(fromDay),
      [recipe.RecipeId]: Utilities.formatMenuItemForRecipeMap(
        recipe,
        recipesById
      ),
    };
    const meals = reimbMealsByDayMap[fromDay];

    const isRecipeOnDay = recipeMap[recipe.RecipeId];

    if (isRecipeOnDay) {
      reimbMealsByDayMap[fromDay] = Utilities.removeRecipeFromMeals({
        meals,
        recipe,
        recipeMap,
      });

      dispatch({
        type: types.REMOVE_RECIPE_FROM_REIMB_MEALS,
        payload: reimbMealsByDayMap,
      });
    }

    // Only add the recipe to the meal if the recipe being combined with is not a la carte
    if (!isParentAlacarte) {
      dispatch(
        addRecipeToReimbMeal({ mealRecipeId, dayNumber: toDay, recipe })
      );
    }
  };

/**
 * Reorders the items in a cycle menu day list based on the item that was
 * moved.
 *
 * @param {Object} droppableSource the droppable source
 * @param {Object} droppableDestination the droppable destination
 * @param {String} draggableId
 */
export const reorderMenuItems =
  ({ droppableSource, droppableDestination, draggableId }) =>
  (dispatch, getState) => {
    const state = getState();
    let menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);
    let recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const viewBy = EditCycleMenuSelectors.getCycleMenuViewBy(state);
    const categories = CommonSelectors.getCategoriesForView(state, viewBy);

    const recipeDraggableIds = menuDaysMap[droppableSource.droppableId];

    const options = { alacarteCategory: viewBy !== VIEW_BY.DAY };

    const mappedRecipes = Utilities.mapIdsToRecipe(
      recipeDraggableIds,
      recipesById
    );

    const recipe = recipesById[draggableId];

    const maxOrderNum = Utilities.maxOrderNumForCategory(
      mappedRecipes,
      recipe.RecipeCategoryId
    );

    const numCategories = Utilities.countCategories(
      mappedRecipes,
      Math.min(droppableDestination.index, maxOrderNum)
    );

    let startIndex = droppableSource.index - numCategories;
    let endIndex = droppableDestination.index - numCategories;

    const reorderedRecipes = Utilities.reorder(
      recipeDraggableIds,
      startIndex,
      endIndex
    );

    menuDaysMap[droppableSource.droppableId] = reorderedRecipes;

    const resultMapAndId = Utilities.updateMapAndId(
      menuDaysMap,
      recipesById,
      categories,
      options
    );

    dispatch({
      type: types.REORDER_MENU_ITEMS,
      payload: {
        menuDaysMap: resultMapAndId.menuDaysMap,
        recipesById: resultMapAndId.recipesById,
      },
    });
  };

/**
 * Adds the recipe to the selected days.
 * @param {Number} menuId the cycle menu id
 * @param {Number} recipeIndex the index of the recipe in the recipe search ist
 * @param {Number} servings the total projected servings
 * @param {Array} selectedDays the list of selected days
 * @param {String} recipeId
 * @param {Boolean} showErrorToast
 * @param {Boolean} copying
 * @returns {Array[Boolean, Error]} true if added, false otherwise
 */
export const addRecipeToDays =
  ({
    menuId,
    recipeIndex,
    recipeId,
    servings,
    selectedDays,
    showErrorToast = true,
  }) =>
  (dispatch, getState) => {
    const state = getState();
    const recipes = getRecipes(state);
    let recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const viewBy = EditCycleMenuSelectors.getCycleMenuViewBy(state);
    const categories = CommonSelectors.getCategoriesForView(state, viewBy);

    const recipeFromSearch = !_.isNil(recipeIndex);
    const recipeFromMenu = !_.isNil(recipeId);

    let recipe = recipes[recipeIndex] || recipesById[recipeId] || null;
    const recipeCountByDayMap = {
      ...EditCycleMenuSelectors.getRecipeCountByDayMap(state),
    };
    const addedRecipes = [];

    if (recipe) {
      if (!Utilities.hasValidServingSize(recipe)) {
        dispatch(
          error(types.SET_ERROR, MESSAGES.ADD_RECIPE_INVALID_SERVING_SIZE)
        );
        return [false, null];
      }

      const menuDaysMap = _.cloneDeep(
        EditCycleMenuSelectors.getMenuDaysMap(state)
      );
      let errorObj = null;
      let added = false;

      selectedDays.forEach((day) => {
        let cycleMenuRecipe = null;
        if (recipeFromSearch) {
          cycleMenuRecipe = Utilities.convertRecipeToMenuItem({
            recipe,
            servings,
            menuId,
            dayNumber: day,
          });
        }

        if (recipeFromMenu) {
          cycleMenuRecipe = {
            ...recipe,
            DayNumber: day,
          };
        }

        if (!cycleMenuRecipe) {
          return;
        }

        const id = shortid.generate();
        const recipeDraggableIds = menuDaysMap[day];
        const recipeCopy = { ...cycleMenuRecipe };
        const foundRecipe = Utilities.findRecipe(
          recipesById,
          cycleMenuRecipe,
          recipeDraggableIds
        );

        if (foundRecipe.reimbursable && foundRecipe.alacarte) {
          errorObj = MESSAGES.ADD_RECIPE_MULTIPLE_ALREADY_ADDED;
          return;
        } else if (foundRecipe.reimbursable && !foundRecipe.alacarte) {
          recipeCopy.DisplayOnCalendar = false;
          recipeCopy.Alacarte = true;
        } else {
          recipeCopy[CycleMenuRecipeFields.DisplayOnCalendar] =
            Utilities.getRecipeDisplayOnMenu(
              recipesById,
              recipeCopy[CycleMenuRecipeFields.RecipeId]
            );
        }

        menuDaysMap[day] = [...recipeDraggableIds, id];

        // copy children
        const children = (recipeCopy.Children || []).map((id) => ({
          ...recipesById[id],
          DayNumber: day,
        }));

        // create ids for children
        const childrenIds = [];
        children.forEach((child) => {
          const id = shortid.generate();
          recipesById[id] = { ...child };
          childrenIds.push(id);
        });

        recipesById[id] = {
          ...recipeCopy,
          DayNumber: day,
          Children: childrenIds,
        };

        Utilities.incrementRecipeCount(
          recipeCountByDayMap,
          day,
          recipeCopy.RecipeId
        );
        addedRecipes.push(recipeCopy);
        added = true;
      });

      const options = { alacarteCategory: viewBy !== VIEW_BY.DAY };

      const result = Utilities.updateMapAndId(
        menuDaysMap,
        recipesById,
        categories,
        options
      );

      dispatch({
        type: types.ADD_RECIPE_TO_DAYS,
        payload: {
          menuDaysMap: result.menuDaysMap,
          recipesById: result.recipesById,
          recipeCountByDayMap,
        },
      });

      addedRecipes.forEach((addedRecipe) => {
        const { DayNumber } = addedRecipe;

        dispatch(
          createAndAddReimbMeal({ recipe: addedRecipe, dayNumber: DayNumber })
        );
      });

      if (errorObj && showErrorToast) {
        dispatch(error(types.SET_ERROR, errorObj));
      }
      return [added, errorObj];
    }
    return [false, null];
  };

export const resetMenu = () => ({
  type: types.RESET_MENU_STATE,
});

export const getCycleMenu = (cycleMenuId) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.CYCLE_MENU_FETCHING, true));
    try {
      const data = {
        CycleMenuId: cycleMenuId,
      };
      const result = await MPWWebApi.postGenericInsertUpdate(
        `CycleMenu/GetCycleMenu/`,
        data
      );
      const resData = result.data.Data;
      const numberOfDays = resData[CycleMenuDetailsFields.NumberOfDaysInCycle];

      const menuDaysMap = Utilities.createDaysMap(numberOfDays);

      const recipesById = {};
      const recipeCountByDayMap = {};

      resData.Details.forEach((item) => {
        if (item.DayNumber <= numberOfDays) {
          const formattedItem = Utilities.convertDetailToMenuItem(
            item,
            resData.Servings
          );

          const children = [];

          if (
            Array.isArray(formattedItem.Children) &&
            formattedItem.Children.length
          ) {
            formattedItem.Children.forEach((item) => {
              const childrenItem = Utilities.convertDetailToMenuItem(
                item,
                resData.Servings
              );
              const id = shortid.generate();
              recipesById[id] = {
                ...childrenItem,
              };
              children.push(id);
            });
          }

          formattedItem.Children = children;

          const id = shortid.generate();
          recipesById[id] = {
            ...formattedItem,
          };
          menuDaysMap[formattedItem.DayNumber].push(id);
          Utilities.incrementRecipeCount(
            recipeCountByDayMap,
            formattedItem.DayNumber,
            formattedItem.RecipeId
          );
        }
      });

      const reimbMealsByDayMap = {};
      resData.ReimbMeals.forEach((reimbMeal) => {
        const dayNumber = reimbMeal.DayNumber;
        const recipeId = reimbMeal.RecipeId;

        if (!reimbMealsByDayMap[dayNumber]) {
          reimbMealsByDayMap[dayNumber] = {};
        }

        reimbMealsByDayMap[dayNumber][recipeId] =
          Utilities.formatApiReimbMeal(reimbMeal);
      });

      dispatch(
        initialize(EDIT_MENU_FORM_NAME, {
          [EditMenuFields.MenuDaysMap]: menuDaysMap,
          [EditMenuFields.RecipesById]: recipesById,
        })
      );
      const categories = CommonSelectors.getCategoriesForView(
        getState(),
        VIEW_BY.MONTH
      );

      const options = { alacarteCategory: true };

      const update = Utilities.updateMapAndId(
        menuDaysMap,
        recipesById,
        categories,
        options
      );

      dispatch(
        success(types.RECEIVED_MENU_DETAILS, {
          menuDaysMap: update.menuDaysMap,
          recipesById: update.recipesById,
          recipeCountByDayMap,
          reimbMealsByDayMap,
          menuId: resData[CycleMenuDetailsFields.Id],
          numberOfDays,
          menuTitle: resData[CycleMenuDetailsFields.Description],
          fiscalYear: resData[CycleMenuDetailsFields.FiscalYear],
          nutritionStandardId: resData[EditMenuFields.NutritionStandardId],
          servings: resData[CycleMenuDetailsFields.Servings],
          analysisDays: resData[CycleMenuDetailsFields.AnalysisDays],
          isReadOnly: resData[CycleMenuDetailsFields.IsReadOnly],
          sharedSourceId: resData[CycleMenuDetailsFields.SharedSourceId],
          editableAppliedDays: {},
        })
      );
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.SET_ERROR, MESSAGES.GET_CYCLE_MENU_ERROR)
      );
    }

    dispatch(loading(types.CYCLE_MENU_FETCHING, false));
  };
};

export const saveCycleMenu = () => {
  return async (dispatch, getState, MPWWebApi) => {
    let saved = false;
    dispatch(loading(types.CYCLE_MENU_FETCHING, true));

    try {
      const state = getState();

      const cycleMenuId = EditCycleMenuSelectors.getMenuId(state);
      const recipesById = EditCycleMenuSelectors.getRecipesById(state);

      const menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);

      const Details = Utilities.flattenMapSorted(menuDaysMap, recipesById);
      const ReimbMeals = EditCycleMenuSelectors.getReimbMealsForApi(state);

      await MPWWebApi.postGenericInsertUpdate(
        `CycleMenu/WriteDetail/${cycleMenuId}`,
        { Details, ReimbMeals }
      );

      dispatch(setSaveSuccessful(true));
      saved = true;
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.CYCLE_MENU_FETCH_ERROR,
          MESSAGES.FETCH_ERROR
        )
      );
    }

    dispatch(loading(types.CYCLE_MENU_FETCHING, false));
    return saved;
  };
};

export const deleteSelectedRecipes = () => (dispatch, getState) => {
  const state = getState();
  const selectedRecipesById = getSelectedRecipesById(state);
  const selectedByIdsSet = new Set(Object.keys(selectedRecipesById));

  if (selectedByIdsSet.size < 1) {
    return false;
  }

  dispatch(deleteRecipes([...selectedByIdsSet]));
  return true;
};

export const deleteRecipes = (recipeIds) => (dispatch, getState) => {
  const state = getState();
  const recipesById = { ...EditCycleMenuSelectors.getRecipesById(state) };
  const recipeCountByDayMap = {
    ...EditCycleMenuSelectors.getRecipeCountByDayMap(state),
  };
  const selectedByIdsSet = new Set(recipeIds.filter(Boolean));
  const reimbMealsByDayMap = {
    ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
  };
  const menuDaysMap = {
    ...EditCycleMenuSelectors.getMenuDaysMap(state),
  };
  const recipeMapByDay = EditCycleMenuSelectors.getRecipeMapByDay(state);

  Object.entries(menuDaysMap).forEach(([day, arr]) => {
    menuDaysMap[day] = arr.filter((id) => !selectedByIdsSet.has(id));
  });

  const childIds = new Set();
  const childIdsToParent = {};

  Object.keys(recipesById).forEach((id) => {
    const Children = recipesById[id].Children;
    const childRecipeIds = Children.filter((id) => selectedByIdsSet.has(id));
    childRecipeIds.forEach((cId) => {
      childIds.add(cId);
      childIdsToParent[cId] = id;
    });

    recipesById[id] = {
      ...recipesById[id],
      Children: recipesById[id].Children.filter(
        (id) => !selectedByIdsSet.has(id)
      ),
    };
  });

  selectedByIdsSet.forEach((id) => {
    const recipe = recipesById[id];
    const { DayNumber, RecipeId } = recipe;
    const meals = { ...reimbMealsByDayMap[DayNumber] };

    Utilities.decrementRecipeCount(recipeCountByDayMap, DayNumber, RecipeId);
    // Alacarte items are not concerned with meals so ignore
    if (!recipe.Alacarte) {
      if (!childIds.has(id)) {
        const recipeMap = recipeMapByDay[DayNumber];

        Object.keys(meals).forEach((k) => {
          let meal = { ...meals[k] };
          meal.Children = { ...meal.Children };

          const entree = recipeMap[meal.RecipeId];

          if (entree) {
            const isChildOfEntree = entree.Children[RecipeId];

            // Do not remove the recipe from the meal when recipe is a child of the entree associated with the meal
            if (!isChildOfEntree) {
              if (!Utilities.areContributionsModifiedByUser(meal, recipeMap)) {
                // Remove recipe from the meal
                delete meal.Children[RecipeId];
                const updatedContributionValues =
                  Utilities.computeMealContributionValues(meal, recipeMap);

                meal = { ...meal, ...updatedContributionValues };
              } else {
                delete meal.Children[RecipeId];
              }

              meals[k] = meal;
              reimbMealsByDayMap[DayNumber] = meals;
            }
          }
        });

        // Remove the meal if its an entree
        delete meals[recipe.RecipeId];
      } else {
        // Remove the recipe from the meal if the recipe isn't on the menu
        const parentDraggableId = childIdsToParent[id];
        const parentRecipe = recipesById[parentDraggableId];
        const parentRecipeId = recipesById[parentDraggableId].RecipeId;

        if (
          _.isNil(recipeCountByDayMap[parentRecipe.DayNumber]) ||
          _.isNil(recipeCountByDayMap[parentRecipe.DayNumber][RecipeId]) ||
          recipeCountByDayMap[parentRecipe.DayNumber][RecipeId] === 0
        ) {
          if (meals[parentRecipeId] && meals[parentRecipeId].Children) {
            let updatedMeal = { ...meals[parentRecipeId] };
            updatedMeal.Children = { ...updatedMeal.Children };
            const recipeMap = recipeMapByDay[parentRecipe.DayNumber];

            if (
              !Utilities.areContributionsModifiedByUser(updatedMeal, recipeMap)
            ) {
              delete updatedMeal.Children[RecipeId];
              const updatedContributionValues =
                Utilities.computeMealContributionValues(updatedMeal, recipeMap);
              updatedMeal = { ...updatedMeal, ...updatedContributionValues };
            } else {
              delete updatedMeal.Children[RecipeId];
            }

            meals[parentRecipeId] = updatedMeal;
            reimbMealsByDayMap[DayNumber] = meals;
          }
        }
      }
    }
    delete recipesById[id];
  });

  dispatch({
    type: types.DELETE_RECIPES,
    payload: {
      menuDaysMap,
      recipesById,
      recipeCountByDayMap,
      reimbMealsByDayMap,
    },
  });
};

export const clearError = () => ({
  type: types.CLEAR_ERROR,
});

export const applyCycleMenu = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.APPLY_CYCLE_MENU, true));
    let result = [false, null];

    try {
      const response = await MPWWebApi.postGenericInsertUpdate(
        'DailyMenu/WriteApplyCycleMenu',
        data
      );
      result = [true, response.data.Data];
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.APPLY_CYCLE_MENU_ERROR,
          MESSAGES.APPLY_CYCLE_MENU_ERROR
        )
      );

      // If response returns Missing OVS, show dialog
      if (
        _.get(ex, 'response.data.Data', '').includes('Missing Offer Vs. Serve')
      ) {
        const message = (
          <div>
            <div>
              <strong>{ex.response.data.Data}</strong>
            </div>
            <div>
              Please visit{' '}
              <a href="/sitesetup" target="_blank">
                Site Setup
              </a>{' '}
              to add missing Offer vs. Serve Meal configurations.
            </div>
          </div>
        );
        const showDigitalMenuSuiteError =
          undefined !==
          Object.keys(data).find((key) => {
            return DIGITAL_MENU_SUITE_OPTIONS.includes(key);
          });

        const notificationTitle = showDigitalMenuSuiteError
          ? NOTIFICATION_DIALOG_MESSAGES.UNABLE_TO_PUBLISH_POS_MISSING_OVS_AND_DIGITIAL_MENU_SUITE
          : NOTIFICATION_DIALOG_MESSAGES.UNABLE_TO_PUBLISH_POS_MISSING_OVS;
        dispatch(
          setNotificationDialog({
            ...notificationTitle,
            message,
          })
        );
      }
      result = [false, null];
    } finally {
      dispatch(loading(types.APPLY_CYCLE_MENU, false));
      return result;
    }
  };
};

export const unApplyCycleMenu = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.APPLY_CYCLE_MENU, true));
    let result = [false, null];

    try {
      const response = await MPWWebApi.postGenericInsertUpdate(
        'DailyMenu/WriteUnApplyCycleMenu',
        data
      );
      result = [true, response.data.Data];
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.APPLY_CYCLE_MENU_ERROR,
          MESSAGES.UN_APPLY_CYCLE_MENU_ERROR
        )
      );

      result = [false, null];
    } finally {
      dispatch(loading(types.APPLY_CYCLE_MENU, false));
      return result;
    }
  };
};

export const checkApplyCycleMenu = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      const response = await MPWWebApi.postGenericInsertUpdate(
        'DailyMenu/CheckApplyCycleMenu',
        data
      );

      return response.data.Data;
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.APPLY_CYCLE_MENU_ERROR,
          MESSAGES.CHECK_CYCLE_MENU_ERROR
        )
      );
    }
    return null;
  };
};

export const changeProjectedServings = (
  recipeDraggableId,
  projectedServings
) => ({
  type: types.UPDATE_RECIPE_PROJECTED_SERVINGS,
  payload: {
    recipeDraggableId,
    projectedServings,
  },
});

export const analyzeCycleMenu = ({
  cycleMenuId,
  analysisDays,
  analyzeDay = 0,
}) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      const { Api, source } = MPWWebApi.createCancelableApi();
      dispatch(loading(types.ANALYZING_CYCLE_MENU, true, true, source));

      const state = getState();
      const recipesById = EditCycleMenuSelectors.getRecipesById(state);
      const menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);

      const cycleMenuDetails = Utilities.flattenMapToRecipe(
        menuDaysMap,
        recipesById
      );

      /** Pull recipe's children */
      const children = cycleMenuDetails.map((d) => d.Children);
      /** Analyze takes it all in as a flat array */
      const Details = _.flattenDeep([cycleMenuDetails, children]).map((d) =>
        Utilities.formatDetailForAnalysis(d)
      );
      const ReimbMeals = EditCycleMenuSelectors.getReimbMealsForApi(state);

      const response = await Api.postGenericInsertUpdate(
        `CycleMenu/AnalyzeCycleMenu/${cycleMenuId}/${analysisDays}/${analyzeDay}`,
        { Details, ReimbMeals }
      );

      const data = response.data.Data;
      const weeklyRequirements = data.Requirements;
      const unmetDailyContributions = data.UnmetDailyContributions;
      const dailyContributions = data.DailyContributions;
      dispatch(success(types.SET_DAILY_CONTRIBUTIONS, dailyContributions));
      dispatch(success(types.SET_WEEKLY_REQUIREMENTS, weeklyRequirements));
      dispatch(
        success(types.SET_UNMET_DAILY_REQUIREMENTS, unmetDailyContributions)
      );
      dispatch(loading(types.ANALYZING_CYCLE_MENU, false));
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.ANALYZE_CYCLE_MENU_ERROR,
          MESSAGES.ANALYZE_CYCLE_MENU_ERROR
        )
      );

      if (!isRequestCancelation(ex)) {
        dispatch(loading(types.ANALYZING_CYCLE_MENU, false));
      }
    }
  };
};

export const changeRecipeDisplayOnCalendar =
  (recipeDraggableId, displayOnCalendar) => (dispatch, getState) => {
    const state = getState();
    const recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const recipe = recipesById[recipeDraggableId];

    if (recipe && recipe[CycleMenuRecipeFields.RecipeId]) {
      const recipeId = recipe[CycleMenuRecipeFields.RecipeId];
      const updatedRecipesById = Utilities.updateRecipeDisplayOnMenu({
        recipesById,
        displayOnCalendar,
        recipeId,
      });

      dispatch({
        type: types.CHANGE_RECIPE_DISPLAY_ON_CALENDAR,
        payload: { recipesById: updatedRecipesById },
      });
    }
  };

export const changeQtyOfPortion =
  (recipeDraggableId, qtyOfPortion) => (dispatch, getState) => {
    const state = getState();
    const recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const reimbMealsByDayMap =
      EditCycleMenuSelectors.getReimbMealsByDayMap(state);

    const recipe = recipesById[recipeDraggableId];
    const { DayNumber } = recipe;

    dispatch({
      type: types.UPDATE_RECIPE_QTY_OF_PORTION,
      payload: {
        recipeDraggableId,
        qtyOfPortion,
      },
    });

    if (!recipe.Alacarte) {
      const meals = reimbMealsByDayMap[DayNumber];
      const recipeMap =
        EditCycleMenuSelectors.getRecipeMapOnDay(state)(DayNumber);

      if (meals) {
        Object.keys(meals).forEach((mealRecipeId) => {
          const meal = meals[mealRecipeId];
          const shouldUpdateContributions =
            !Utilities.areContributionsModifiedByUser(meal, recipeMap);

          if (shouldUpdateContributions) {
            dispatch(
              updateReimbMealContributions({
                dayNumber: DayNumber,
                recipeId: mealRecipeId,
              })
            );
          }
        });
      }
    }
  };

export const changeServingsPercentage = (recipeDraggableId, percentage) => ({
  type: types.UPDATE_RECIPE_SERVINGS_PERCENTAGE,
  payload: {
    recipeDraggableId,
    percentage,
  },
});

export const toggleRecipeAlacarte =
  (recipeDraggableId) => (dispatch, getState) => {
    const state = getState();
    const recipesById = { ...EditCycleMenuSelectors.getRecipesById(state) };
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };

    const updatedRecipe = { ...recipesById[recipeDraggableId] };
    const dayNumber = updatedRecipe.DayNumber;
    const recipeMap =
      EditCycleMenuSelectors.getRecipeMapOnDay(state)(dayNumber);
    const isAlacarte = !updatedRecipe.Alacarte;
    updatedRecipe.Alacarte = isAlacarte;

    recipesById[recipeDraggableId] = updatedRecipe;
    updatedRecipe.Children.forEach((id) => {
      recipesById[id] = {
        ...recipesById[id],
        Alacarte: updatedRecipe.Alacarte,
      };
    });
    const recipeId = updatedRecipe.RecipeId;

    const meals = { ...reimbMealsByDayMap[dayNumber] };

    if (updatedRecipe.RecipeCategoryId !== RecipeCategoryEnum.Entree) {
      if (isAlacarte) {
        reimbMealsByDayMap[dayNumber] = Utilities.removeRecipeFromMeals({
          meals,
          recipe: updatedRecipe,
          recipeMap,
        });
      } else {
        reimbMealsByDayMap[dayNumber] = Utilities.addRecipeToMeals({
          meals,
          recipe: updatedRecipe,

          // Recipe map will not include the recipe because it was an a la carte item beforehand so add it to the map
          recipeMap: { ...recipeMap, [updatedRecipe.RecipeId]: updatedRecipe },
        });
      }
    } else {
      if (isAlacarte) {
        delete meals[recipeId];
      } else {
        // Convert the a la carte entree to a reimbursable meal
        const recipesOnDay =
          EditCycleMenuSelectors.getNonEntreeAndNonAlaCarteRecipesOnDay(
            state,
            dayNumber
          );
        const childRecipes = updatedRecipe.Children.map(
          (recipeDraggableId) => recipesById[recipeDraggableId]
        ).concat(recipesOnDay);

        meals[recipeId] = Utilities.convertRecipeToReimbMeal(
          updatedRecipe,
          dayNumber,
          childRecipes
        );
      }
      reimbMealsByDayMap[dayNumber] = meals;
    }

    dispatch({
      type: types.TOGGLE_RECIPE_ALACARTE,
      payload: { recipesById, reimbMealsByDayMap },
    });
  };

export const addToMenuCombineRecipe =
  ({ to, toDay, recipeIndex, menuId, servings }) =>
  (dispatch, getState) => {
    const state = getState();
    const recipes = getRecipes(state);
    let recipesById = EditCycleMenuSelectors.getRecipesById(state);
    let menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);
    const categories = CommonSelectors.getRecipesCategoriesWithNull(state);
    const recipeCountByDayMap =
      EditCycleMenuSelectors.getRecipeCountByDayMap(state);
    const recipe = recipes[recipeIndex] || null;

    const cycleMenuRecipe = Utilities.convertRecipeToMenuItem({
      menuId,
      recipe,
      dayNumber: toDay,
      servings,
    });

    const parentCopy = {
      ...recipesById[to],
    };

    const validAttachment = Utilities.isValidAttachment(
      parentCopy,
      cycleMenuRecipe,
      recipesById
    );

    if (!validAttachment) {
      return false;
    }

    const id = shortid.generate();

    cycleMenuRecipe.ProjectedServings = parentCopy.ProjectedServings;
    cycleMenuRecipe.ServingsPercentage = String(
      Utilities.calculateServingsPercentage({
        projectedServings: parentCopy.ProjectedServings,
        servings,
      })
    );
    cycleMenuRecipe.Alacarte = parentCopy.Alacarte;

    recipesById[id] = cycleMenuRecipe;
    parentCopy.Children.push(id);

    const result = Utilities.updateMapAndId(
      menuDaysMap,
      recipesById,
      categories
    );
    const parentRecipeId = parentCopy.RecipeId;

    dispatch({
      type: types.REMOVE_RECIPE_CHILD,
      payload: {
        menuDaysMap: result.menuDaysMap,
        recipesById: result.recipesById,
        recipeCountByDayMap,
      },
    });
    dispatch(
      addRecipeToReimbMeal({
        mealRecipeId: parentRecipeId,
        recipe: cycleMenuRecipe,
        dayNumber: parentCopy.DayNumber,
      })
    );
    return true;
  };

export const removeChild = (childId, parentId) => (dispatch, getState) => {
  const state = getState();
  const servings = EditCycleMenuSelectors.getServings(state);
  let recipesById = EditCycleMenuSelectors.getRecipesById(state);
  let menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);
  const viewBy = EditCycleMenuSelectors.getCycleMenuViewBy(state);
  const categories = CommonSelectors.getCategoriesForView(state, viewBy);
  const childToBeRemoved = recipesById[childId];
  const parentRecipe = recipesById[parentId];
  const recipeCountByDayMap = {
    ...EditCycleMenuSelectors.getRecipeCountByDayMap(state),
  };
  const dayNumber = childToBeRemoved.DayNumber;

  const foundSameRecipe = menuDaysMap[dayNumber].find((id) => {
    return (
      recipesById[id].RecipeId === childToBeRemoved.RecipeId &&
      recipesById[id].Alacarte === parentRecipe.Alacarte
    );
  });
  let copy = {
    ...recipesById[childId],
    Alacarte: parentRecipe.Alacarte,
  };

  if (foundSameRecipe) {
    copy = {
      ...recipesById[foundSameRecipe],
      Alacarte: parentRecipe.Alacarte,
    };
    copy.ProjectedServings = _.sum([
      copy.ProjectedServings,
      childToBeRemoved.ProjectedServings,
    ]);

    copy.ServingsPercentage = String(
      Utilities.calculateServingsPercentage({
        projectedServings: copy.ProjectedServings,
        servings,
      })
    );

    recipesById[foundSameRecipe] = {
      ...copy,
    };
    delete recipesById[childId];
  } else {
    recipesById[childId] = copy;
    menuDaysMap[childToBeRemoved.DayNumber].push(childId);
  }

  const parentCopy = {
    ...parentRecipe,
  };

  parentCopy.Children = parentCopy.Children.filter((id) => id !== childId);

  recipesById[parentId] = {
    ...parentCopy,
  };

  const options = { alacarteCategory: viewBy !== VIEW_BY.DAY };

  const result = Utilities.updateMapAndId(
    menuDaysMap,
    recipesById,
    categories,
    options
  );

  recipesById = result.recipesById;
  menuDaysMap = result.menuDaysMap;

  const recipeId = childToBeRemoved.RecipeId;
  const recipeCount = menuDaysMap[dayNumber].filter((id) => {
    return recipesById[id].RecipeId === recipeId;
  }).length;

  recipeCountByDayMap[dayNumber] = {
    ..._.get(recipeCountByDayMap, dayNumber, {}),
  };
  _.set(recipeCountByDayMap, [dayNumber, recipeId], recipeCount);

  dispatch({
    type: types.REMOVE_RECIPE_CHILD,
    payload: {
      menuDaysMap,
      recipesById,
      recipeCountByDayMap,
    },
  });
  dispatch(addRecipeToReimbMeals({ recipe: childToBeRemoved, dayNumber }));
};

export const switchCycleMenuView = (viewBy) => (dispatch, getState) => {
  const state = getState();

  let recipesById = {
    ...EditCycleMenuSelectors.getRecipesById(state),
  };
  let menuDaysMap = {
    ...EditCycleMenuSelectors.getMenuDaysMap(state),
  };

  const categories = CommonSelectors.getCategoriesForView(state, viewBy);

  const options = { alacarteCategory: viewBy !== VIEW_BY.DAY };

  const result = Utilities.updateMapAndId(
    menuDaysMap,
    recipesById,
    categories,
    options
  );

  dispatch({
    type: types.CYCLE_MENU_SWITCH_VIEW,
    payload: {
      recipesById: result.recipesById,
      menuDaysMap: result.menuDaysMap,
      viewBy,
    },
  });
};

export const disableDeleteRecipe = (disable) => {
  return {
    type: types.DISABLE_DELETE_RECIPE,
    payload: { disable },
  };
};

export const addReimbMeal =
  ({ recipe, dayNumber }) =>
  (dispatch, getState) => {
    const state = getState();

    if (
      recipe.RecipeCategoryId === RecipeCategoryEnum.Entree &&
      !recipe.Alacarte
    ) {
      const mealExists = Boolean(
        EditCycleMenuSelectors.getReimbMealOnDay(state, dayNumber, recipe.Id)
      );

      if (!mealExists) {
        const recipesOnDay =
          EditCycleMenuSelectors.getNonEntreeAndNonAlaCarteRecipesOnDay(
            state,
            dayNumber
          );
        const reimbMeal = Utilities.convertRecipeToReimbMeal(
          recipe,
          dayNumber,
          recipesOnDay
        );

        dispatch({
          type: types.ADD_REIMB_MEAL,
          payload: {
            dayNumber,
            reimbMeal,
          },
        });
      }
    }
  };

export const addRecipeToReimbMeals =
  ({ recipe, dayNumber }) =>
  (dispatch, getState) => {
    const state = getState();

    if (
      recipe.RecipeCategoryId !== RecipeCategoryEnum.Entree &&
      !recipe.Alacarte
    ) {
      const reimbMealsByDayMap = {
        ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
      };
      const meals = reimbMealsByDayMap[dayNumber];

      if (meals) {
        // Include the recipe that's being added in the map
        const recipeMap = {
          ...EditCycleMenuSelectors.getRecipeMapOnDay(state)(dayNumber),
          [recipe.RecipeId]: recipe,
        };
        const newMeals = Utilities.addRecipeToMeals({
          meals,
          recipe,
          recipeMap,
        });
        reimbMealsByDayMap[dayNumber] = newMeals;

        dispatch({
          type: types.ADD_RECIPE_TO_REIMB_MEALS,
          payload: reimbMealsByDayMap,
        });
      }
    }
  };

export const moveReimbMealRecipe =
  (fromDay, toDay, recipe) => (dispatch, getState) => {
    const state = getState();
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };
    const recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const fromDayMeals = reimbMealsByDayMap[fromDay] || {};
    const toDayMeals = reimbMealsByDayMap[toDay] || {};
    const { RecipeId } = recipe;
    const fromDayRecipeMap = {
      ...EditCycleMenuSelectors.getRecipeMapOnDay(state)(fromDay),
      // Add the recipe that's being moved to this map since it will have already been removed
      // from the day by the previous action
      [recipe.RecipeId]: recipe,
    };
    const toDayRecipeMap = {
      ...EditCycleMenuSelectors.getRecipeMapOnDay(state)(toDay),
      [recipe.RecipeId]: recipe,
    };

    if (recipe.RecipeCategoryId !== RecipeCategoryEnum.Entree) {
      reimbMealsByDayMap[fromDay] = Utilities.removeRecipeFromMeals({
        meals: fromDayMeals,
        recipe,
        recipeMap: fromDayRecipeMap,
      });
      reimbMealsByDayMap[toDay] = Utilities.addRecipeToMeals({
        meals: toDayMeals,
        recipe,
        recipeMap: toDayRecipeMap,
      });
    } else {
      // Entree is being moved
      const recipesOnDay =
        EditCycleMenuSelectors.getNonEntreeAndNonAlaCarteRecipesOnDay(
          state,
          toDay
        );
      const currentChildrenIds = recipe.Children.map((recipeDraggableId) => {
        return recipesById[recipeDraggableId].RecipeId;
      });
      const mealToMove = fromDayMeals[RecipeId];
      mealToMove.DayNumber = parseInt(toDay, 10);

      const Children = {};
      recipesOnDay.forEach((r) => {
        if (!r.Alacarte) {
          Children[r.RecipeId] = r.RecipeId;
        }
      });
      currentChildrenIds.forEach((recipeId) => (Children[recipeId] = recipeId));

      // If the meal doesn't already exist on that day, add it
      if (!toDayMeals[RecipeId]) {
        toDayMeals[RecipeId] = { ...mealToMove, Children };
      }

      delete fromDayMeals[RecipeId];

      reimbMealsByDayMap[fromDay] = fromDayMeals;
      reimbMealsByDayMap[toDay] = toDayMeals;
    }
    dispatch({
      type: types.MOVE_REIMB_MEAL_RECIPE,
      payload: reimbMealsByDayMap,
    });
  };

export const changeReimbMealContribution =
  ({ field, recipeId, value, dayNumber, hasVegTotalBeenEdited }) =>
  (dispatch, getState) => {
    const state = getState();
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };
    const meal = {
      ...reimbMealsByDayMap[dayNumber][recipeId],
      [field]: value,
    };

    reimbMealsByDayMap[dayNumber] = {
      ...reimbMealsByDayMap[dayNumber],
      [recipeId]: Utilities.updateMealContributionTotals(
        meal,
        field,
        hasVegTotalBeenEdited
      ),
    };

    dispatch({
      type: types.UPDATE_REIMB_MEAL_CONTRIBUTION,
      payload: reimbMealsByDayMap,
    });
  };

export const addRecipeToReimbMeal =
  ({ mealRecipeId, dayNumber, recipe }) =>
  (dispatch, getState) => {
    const state = getState();
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };
    const recipeMap = {
      ...EditCycleMenuSelectors.getRecipeMapOnDay(state)(dayNumber),
      [recipe.RecipeId]: recipe,
    };
    const mealsOnDay = { ...reimbMealsByDayMap[dayNumber] };
    const mealsToUpdate = { [mealRecipeId]: mealsOnDay[mealRecipeId] };

    const updatedMeals = Utilities.addRecipeToMeals({
      meals: mealsToUpdate,
      recipe,
      recipeMap,
    });
    mealsOnDay[mealRecipeId] = updatedMeals[mealRecipeId];
    reimbMealsByDayMap[dayNumber] = mealsOnDay;

    dispatch({
      type: types.ADD_RECIPE_TO_REIMB_MEAL,
      payload: reimbMealsByDayMap,
    });
  };

export const removeRecipeFromReimbMeal =
  ({ mealRecipeId, dayNumber, recipe }) =>
  (dispatch, getState) => {
    const state = getState();
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };
    const recipesById = EditCycleMenuSelectors.getRecipesById(state);
    const recipeMap = {
      ...EditCycleMenuSelectors.getRecipeMapOnDay(state)(dayNumber),
      [recipe.RecipeId]: Utilities.formatMenuItemForRecipeMap(
        recipe,
        recipesById
      ),
    };
    const mealsOnDay = { ...reimbMealsByDayMap[dayNumber] };
    const mealsToUpdate = { [mealRecipeId]: mealsOnDay[mealRecipeId] };

    const updatedMeals = Utilities.removeRecipeFromMeals({
      meals: mealsToUpdate,
      recipe,
      recipeMap,
    });
    mealsOnDay[mealRecipeId] = updatedMeals[mealRecipeId];
    reimbMealsByDayMap[dayNumber] = mealsOnDay;

    dispatch({
      type: types.REMOVE_RECIPE_FROM_REIMB_MEALS,
      payload: reimbMealsByDayMap,
    });
  };

export const createAndAddReimbMeal =
  ({ recipe, dayNumber }) =>
  (dispatch, getState) => {
    dispatch(addReimbMeal({ recipe, dayNumber }));
    dispatch(addRecipeToReimbMeals({ recipe, dayNumber }));
  };

export const updateReimbMealContributions =
  ({ dayNumber, recipeId }) =>
  (dispatch, getState) => {
    const state = getState();
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };
    const recipeMap =
      EditCycleMenuSelectors.getRecipeMapOnDay(state)(dayNumber);

    let meal = { ...reimbMealsByDayMap[dayNumber][recipeId] };
    meal = {
      ...meal,
      ...Utilities.computeMealContributionValues(meal, recipeMap),
    };
    reimbMealsByDayMap[dayNumber] = { ...reimbMealsByDayMap[dayNumber] };
    reimbMealsByDayMap[dayNumber][recipeId] = meal;

    dispatch({
      type: types.UPDATE_REIMB_MEAL_CONTRIBUTION,
      payload: reimbMealsByDayMap,
    });
  };

export const resetReimbMealContributions =
  ({ dayNumber, recipeId }) =>
  (dispatch, getState) => {
    const state = getState();
    const reimbMealsByDayMap = {
      ...EditCycleMenuSelectors.getReimbMealsByDayMap(state),
    };
    const recipeMap =
      EditCycleMenuSelectors.getRecipeMapOnDay(state)(dayNumber);

    const currentMeal = { ...reimbMealsByDayMap[dayNumber][recipeId] };
    const updatedMeal = {
      ...currentMeal,
      ...Utilities.computeMealContributionValues(currentMeal, recipeMap),
    };
    reimbMealsByDayMap[dayNumber] = { ...reimbMealsByDayMap[dayNumber] };
    reimbMealsByDayMap[dayNumber][recipeId] = updatedMeal;

    dispatch({
      type: types.UPDATE_REIMB_MEAL_CONTRIBUTION,
      payload: reimbMealsByDayMap,
    });
  };

export const getAppliedMenu = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.CYCLE_MENU_FETCHING, true));
      const result = await MPWWebApi.postGenericInsertUpdate(
        `DailyMenu/GetAppliedMenu`,
        data
      );
      const startDate = moment(data[GetAppliedMenuFields.StartDate]);
      const endDate = moment(data[GetAppliedMenuFields.EndDate]);
      const numberOfDays =
        moment.duration(endDate.diff(startDate)).asDays() + 1;
      const resData = result.data.Data;
      const menuDaysMap = Utilities.createDaysMap(numberOfDays);

      const recipesById = {};
      const recipeCountByDayMap = {};
      const editableAppliedDays = {};

      resData.Details.forEach((item) => {
        if (item.DayNumber <= numberOfDays) {
          const formattedItem = Utilities.convertDetailToMenuItem(
            item,
            resData.Servings
          );
          editableAppliedDays[item.DayNumber] = true;

          const children = [];

          if (
            Array.isArray(formattedItem.Children) &&
            formattedItem.Children.length
          ) {
            formattedItem.Children.forEach((item) => {
              const childrenItem = Utilities.convertDetailToMenuItem(
                item,
                resData.Servings
              );
              const id = shortid.generate();
              recipesById[id] = {
                ...childrenItem,
              };
              children.push(id);
            });
          }

          formattedItem.Children = children;

          const id = shortid.generate();
          recipesById[id] = {
            ...formattedItem,
          };
          menuDaysMap[formattedItem.DayNumber].push(id);
          Utilities.incrementRecipeCount(
            recipeCountByDayMap,
            formattedItem.DayNumber,
            formattedItem.RecipeId
          );
        }
      });

      const reimbMealsByDayMap = {};
      resData.ReimbMeals.forEach((reimbMeal) => {
        const dayNumber = reimbMeal.DayNumber;
        const recipeId = reimbMeal.RecipeId;

        if (!reimbMealsByDayMap[dayNumber]) {
          reimbMealsByDayMap[dayNumber] = {};
        }

        reimbMealsByDayMap[dayNumber][recipeId] =
          Utilities.formatApiReimbMeal(reimbMeal);
      });

      dispatch(
        initialize(EDIT_MENU_FORM_NAME, {
          [EditMenuFields.MenuDaysMap]: menuDaysMap,
          [EditMenuFields.RecipesById]: recipesById,
        })
      );
      const categories = CommonSelectors.getCategoriesForView(
        getState(),
        VIEW_BY.MONTH
      );

      const options = { alacarteCategory: true };

      const update = Utilities.updateMapAndId(
        menuDaysMap,
        recipesById,
        categories,
        options
      );

      dispatch(
        success(types.RECEIVED_MENU_DETAILS, {
          menuDaysMap: update.menuDaysMap,
          recipesById: update.recipesById,
          recipeCountByDayMap,
          reimbMealsByDayMap,
          menuId: data[GetAppliedMenuFields.CycleMenuId],
          numberOfDays,
          menuTitle: resData[CycleMenuDetailsFields.Description],
          fiscalYear: resData[CycleMenuDetailsFields.FiscalYear],
          nutritionStandardId: resData[EditMenuFields.NutritionStandardId],
          servings: resData[CycleMenuDetailsFields.Servings],
          analysisDays: Number(data[GetAppliedMenuFields.AnalysisDays]),
          editableAppliedDays,
        })
      );
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.SET_ERROR, MESSAGES.GET_CYCLE_MENU_ERROR)
      );
    } finally {
      dispatch(loading(types.CYCLE_MENU_FETCHING, false));
    }
  };
};

export const saveAppliedMenu =
  ({
    startDate,
    endDate,
    siteId,
    cycleMenuId,
    nutritionStandardId,
    description,
  }) =>
  async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.CYCLE_MENU_FETCHING, true));
      const state = getState();

      const recipesById = EditCycleMenuSelectors.getRecipesById(state);

      const menuDaysMap = EditCycleMenuSelectors.getMenuDaysMap(state);

      const Details = Utilities.flattenMapSorted(menuDaysMap, recipesById);
      const ReimbMeals = EditCycleMenuSelectors.getReimbMealsForApi(state);

      const data = {
        [WriteAppliedMenuFields.Details]: Details,
        [WriteAppliedMenuFields.ReimbMeals]: ReimbMeals,
        [WriteAppliedMenuFields.StartDT]: startDate,
        [WriteAppliedMenuFields.EndDT]: endDate,
        [WriteAppliedMenuFields.CycleMenuId]: cycleMenuId,
        [WriteAppliedMenuFields.SiteId]: siteId,
        [WriteAppliedMenuFields.NutritionStdId]: nutritionStandardId,
        [WriteAppliedMenuFields.MenuDescription]: description,
      };

      await MPWWebApi.postGenericInsertUpdate(
        'DailyMenu/WriteAppliedMenu',
        data
      );

      dispatch(setSaveSuccessful(true));
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.CYCLE_MENU_FETCH_ERROR,
          MESSAGES.FETCH_ERROR
        )
      );
    } finally {
      dispatch(loading(types.CYCLE_MENU_FETCHING, false));
    }
  };
export const replaceRecipes = (recipeIds, recipe) => (dispatch, getState) => {
  const state = getState();
  const recipesById = _.cloneDeep(EditCycleMenuSelectors.getRecipesById(state));
  let reimbMealsByDayMap = _.cloneDeep(
    EditCycleMenuSelectors.getReimbMealsByDayMap(state)
  );
  const recipeMapByDay = _.cloneDeep(
    EditCycleMenuSelectors.getRecipeMapByDay(state)
  );
  const recipeCountByDayMap = _.cloneDeep(
    EditCycleMenuSelectors.getRecipeCountByDayMap(state)
  );
  // Pull out fields that should just use the existing recipe's values
  const {
    Children,
    DayNumber,
    ProjectedServings,
    CycleMenuId,
    ServingsPercentage,
    ...cycleMenuRecipe
  } = Utilities.convertRecipeToMenuItem({ recipe });

  recipeIds.forEach((rId) => {
    const oldRecipe = recipesById[rId];
    const recipeId = oldRecipe[CycleMenuRecipeFields.RecipeId];
    const replacementRecipe = { ...oldRecipe, ...cycleMenuRecipe };
    const oldRecCategoryId = oldRecipe[CycleMenuRecipeFields.RecipeCategoryId];
    const recCategoryId =
      replacementRecipe[CycleMenuRecipeFields.RecipeCategoryId];
    const day = oldRecipe[CycleMenuRecipeFields.DayNumber];
    const recipeMap = recipeMapByDay[day];

    reimbMealsByDayMap[day] = Utilities.removeRecipeFromMeals({
      meals: reimbMealsByDayMap[day],
      recipe: oldRecipe,
      recipeMap,
    });

    // Update the recipe map for the day so that contributions can be computed correctly
    delete recipeMap[recipeId];
    Utilities.decrementRecipeCount(recipeCountByDayMap, day, recipeId);

    // Remove the old reimbursable meal if there is one
    if (ProdRecordHelpers.isEntree(oldRecCategoryId)) {
      delete reimbMealsByDayMap[day][recipeId];
    }

    if (ProdRecordHelpers.isEntree(recCategoryId)) {
      let recipesOnDay = Object.values(recipeMap);
      const itemsAttachedToEntree = oldRecipe[CycleMenuRecipeFields.Children];

      // Add any recipes that are attached to the entree
      if (
        Array.isArray(itemsAttachedToEntree) &&
        itemsAttachedToEntree.length > 0
      ) {
        const additionalRecipes = itemsAttachedToEntree.map(
          (recShortId) => recipesById[recShortId]
        );
        recipesOnDay = recipesOnDay.concat(additionalRecipes);
      }

      // Remove entrees to avoid calculating contributions in the children
      // with the entree itself or other entree recipes
      recipesOnDay = recipesOnDay.filter(
        (rec) =>
          !ProdRecordHelpers.isEntree(
            rec[CycleMenuRecipeFields.RecipeCategoryId]
          )
      );

      const reimbMeal = Utilities.convertRecipeToReimbMeal(
        replacementRecipe,
        day,
        recipesOnDay
      );
      reimbMealsByDayMap = Utilities.addReimbMeal(
        reimbMealsByDayMap,
        reimbMeal,
        day
      );
    } else {
      reimbMealsByDayMap[day] = Utilities.addRecipeToMeals({
        meals: reimbMealsByDayMap[day],
        recipe: replacementRecipe,
        recipeMap,
      });
    }
    recipesById[rId] = replacementRecipe;
    Utilities.incrementRecipeCount(
      recipeCountByDayMap,
      day,
      replacementRecipe[CycleMenuRecipeFields.RecipeId]
    );
  });

  dispatch({
    type: types.REPLACE_RECIPES,
    payload: { recipesById, reimbMealsByDayMap, recipeCountByDayMap },
  });
};

export const getSchoolCalendar = (siteId) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      const response = await MPWWebApi.getGeneric(
        `SchoolCalendar/BySite/${siteId}`
      );

      dispatch(success(types.GET_SCHOOL_CALENDAR, response.data.Data));
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.SET_ERROR,
          MESSAGES.GET_SCHOOL_CALENDAR_ERROR
        )
      );
    }
  };
};

export const getCycleMenuSites = (menuId) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      const response = await MPWWebApi.getGeneric(
        `DailyMenu/GetSitesByCycleMenu/${menuId}`
      );

      dispatch(success(types.GET_CYCLE_MENU_SITES, response.data.Data));
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.SET_ERROR,
          MESSAGES.GET_CYCLE_MENU_SITES_ERROR
        )
      );
    }
  };
};



// WEBPACK FOOTER //
// ./src/pages/managemenu/state/editmenu/actions.js